import { z } from "zod";

/**
 * Nesne paylaşım şemaları (CLOS-250 `[143]`, model: ADR-0033).
 *
 * Kartın saydığı nesneler: versiyon, madde, paragraf, revizyon, yorum, dosya, müzakere.
 * Yorumun kendi kanalı CLOS-153'te. Hedef: sözleşmeye katılmış organizasyon. İzin seti
 * view/comment — reshare ve manage devredilemez, bilinmeyen izin reddedilir.
 *
 * ⚠ PARAGRAF LİSTEDE YOK ve bu bir eksik değil, bir KARAR BEKLEME.
 *
 * Belge modelinde blokların KİMLİĞİ yok: madde kimliği istemci tarafından üretiliyor ve
 * taşınsa bile aynı kalıyor, blok ise yalnız madde içindeki sırasıyla var. Paylaşımı
 * "madde + sıra" ile çapalamak, `object_id` alanına bir kodlama İCAT ETMEK olurdu; bloğa
 * kimlik eklemek ise belge şemasını, editörü, karşılaştırmayı ve şablon değişkenlerini
 * birden değiştirir. İkisi de ürün kararı — kart zaten *"nesne permission veri modeli
 * güvenlik ADR'sinde belirlenir"* diyor. Karar gelene kadar paragraf paylaşımı AÇILMIYOR:
 * yarım bir çapa, düzenleme sonrası sessizce BAŞKA bir paragrafı paylaşır.
 */

export const SHAREABLE_OBJECT_TYPES = [
  "version",
  "clause",
  "work_item",
  "file",
  /** Revizyon önerisi (CLOS-158 ailesi): kendi kimliği ve değişmez metni var. */
  "revision",
  /** Müzakere akışı (CLOS-252): kendi kimliği ve durum makinesi var. */
  "negotiation",
] as const;
export type ShareableObjectType = (typeof SHAREABLE_OBJECT_TYPES)[number];

export const OBJECT_SHARE_PERMISSIONS = ["view", "comment"] as const;

/**
 * Nesne paylaşımı izinlerinin EKRAN METİNLERİ (CLOS-873).
 *
 * ⚠ Bunlar KATALOG ANAHTARI DEĞİL — `findPermission("view")` boş döner. Yetki
 * kataloğunun sözlüğünü buraya uygulamak, ekranda ham `view` bırakırdı. İki ayrı
 * kelime dağarcığı var ve karıştırılmamalı: katalog anahtarı (`contract.view`) ile
 * nesne paylaşımı izni (`view`) aynı şey değil.
 */
export const OBJECT_SHARE_PERMISSION_NAMES: Record<
  (typeof OBJECT_SHARE_PERMISSIONS)[number],
  string
> = {
  view: "Görüntüleme",
  comment: "Yorum",
};

export const createObjectShareSchema = z.strictObject({
  objectType: z.enum(SHAREABLE_OBJECT_TYPES, "Geçerli bir nesne türü seçilmelidir"),
  objectId: z.string().trim().min(1, "Nesne kimliği zorunludur"),
  versionId: z.uuid("Geçerli bir versiyon seçilmelidir").optional(),
  targetTenantId: z.uuid("Geçerli bir organizasyon seçilmelidir"),
  permissions: z
    .array(z.enum(OBJECT_SHARE_PERMISSIONS, "Bu izin paylaşılamaz"))
    .min(1, "En az bir izin seçilmelidir")
    .default(["view"]),
  includeHistory: z.enum(["none", "full"]).default("none"),
});
export type CreateObjectShareInput = z.infer<typeof createObjectShareSchema>;

export type ObjectShareStatus = "active" | "superseded" | "revoked";

/** Kapsam değişikliği — eski kayıt ÜZERİNE YAZILMAZ, yeni revizyon açılır (CLOS-251). */
export const updateObjectShareSchema = z.strictObject({
  permissions: z
    .array(z.enum(OBJECT_SHARE_PERMISSIONS, "Bu izin paylaşılamaz"))
    .min(1, "En az bir izin seçilmelidir")
    .optional(),
  includeHistory: z.enum(["none", "full"]).optional(),
});
export type UpdateObjectShareInput = z.infer<typeof updateObjectShareSchema>;

export const revokeObjectShareSchema = z.strictObject({
  reason: z.string().trim().max(500, "Gerekçe en fazla 500 karakter olabilir").optional(),
});

export type ContractObjectShareView = {
  id: string;
  contractId: string;
  objectType: ShareableObjectType;
  objectId: string;
  versionId: string | null;
  targetTenantId: string;
  permissions: string[];
  includeHistory: "none" | "full";
  status: ObjectShareStatus;
  revokedAt: string | null;
  sharedByName: string;
  createdAt: string;
};

/** Alıcıya giden GÜVENLİ görünüm — adapter'ın beyaz-listeli maskesi (ADR-0033). */
export type SharedObjectView = {
  shareId: string;
  objectType: ShareableObjectType;
  objectId: string;
  permissions: string[];
  /** İçerik adapter'a göre değişir; yalnız beyaz-listeli alanlar taşınır. */
  content: Record<string, unknown>;
};

/**
 * SÖZLEŞMEYİ MÜVEKKİL ALANINA AÇMA (FAZ 0B, ADR-0038).
 *
 * ⚠ HEDEF ALAN, KİŞİ DEĞİL: gövde bir kullanıcı ya da üyelik değil, Müvekkil Alanının
 * kiracı kimliğini taşıyor. Kişi taşısaydı her temsilci için ayrı işlem gerekir ve
 * talimatın 9. maddesi ("kişi bazlı yeniden paylaşım gerekmemeli") çiğnenirdi.
 */
export const openContractToClientAreaSchema = z.strictObject({
  workspaceTenantId: z.uuid("Geçerli bir Müvekkil Alanı seçilmelidir"),
});
export type OpenContractToClientAreaInput = z.infer<typeof openContractToClientAreaSchema>;
